import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Defines the type of content to display in the sidebar panel.
 * - 'sessions': Shows the sessions list (SessionsBar)
 * - 'evidence': Shows the chat history (EvidencePanelContent)
 * - null: No content, sidebar is hidden
 */
export type SidebarPanelContent = 'sessions' | 'evidence' | null;

/**
 * Non-null content type for pinned state (must have a value).
 */
export type SidebarPanelPinnedContent = Exclude<SidebarPanelContent, null>;

/**
 * Maps route path prefixes to their corresponding sidebar content.
 * Used to derive pinned content from the current route.
 */
export const ROUTE_TO_SIDEBAR_CONTENT: Array<{
  pathPrefix: string;
  content: SidebarPanelPinnedContent;
}> = [
  { pathPrefix: '/scribe/session', content: 'sessions' },
  { pathPrefix: '/scribe/chat', content: 'evidence' },
  { pathPrefix: '/library', content: 'evidence' },
  { pathPrefix: '/scribe/calculators', content: 'evidence' },
  { pathPrefix: '/evidence/cpd', content: 'evidence' },
];

/**
 * Atom tracking which content should be displayed in the sidebar panel.
 * Set to 'sessions' or 'evidence' when hovering over the corresponding nav item,
 * null when no content should be displayed.
 *
 * Hover state is derived from this: isHovered = hoverContent !== null
 */
export const sidebarPanelContentAtom = atom<SidebarPanelContent>(null);

/**
 * Atom tracking whether the sidebar panel is pinned open.
 * When pinned, the sidebar stays visible without requiring hover.
 * Persisted to localStorage so the setting survives page refreshes.
 */
export const sidebarPanelPinnedAtom = atomWithStorage<boolean>(
  'heidi-sidebar-panel-pinned',
  true
);

/**
 * Count of open overlays that belong to the sidebar panel.
 *
 * Use this when a component knows its own open/close state.
 * `useSidebarPanelOverlayLock(isOpen)` updates this automatically.
 * While the count is `> 0`, the sessions panel should not hide or switch.
 */
export const sidebarPanelOverlayLockAtom = atom(0);

/**
 * Temporary "overlay teardown in progress" flag for sidebar portals.
 *
 * Use this in sidebar overlay utilities when watching DOM/portal cleanup.
 * Do not use this for component open/close state.
 *
 * Access patterns:
 * - **Non-React code** (MutationObserver callbacks, imperative utilities):
 *   use `getDefaultStore().get()` / `.set()` from `jotai`.
 * - **React hooks** (event handlers, effect callbacks):
 *   use `const store = useStore()` then `store.get()` / `store.set()`.
 *
 * `useAtom()` is intentionally avoided because reads happen inside
 * callbacks (mouseLeave handlers, timers). A `useAtom` value is captured
 * at render time and would be stale by the time the callback fires.
 * `store.get()` always reads the current value at call time.
 */
export const sidebarOverlayActiveAtom = atom(false);

/**
 * Reactive atom tracking whether the sidebar panel feature flag is enabled.
 *
 * Written by ResizableLayout (which reads the PostHog flag at render time)
 * so that launchModalsConfigAtom can subscribe and re-derive immediately
 * when the value changes — without waiting for GlobalModalsWrapper's
 * batched useEffect to propagate through appStateForModalsAtom.
 */
export const sidebarPanelFeatureEnabledAtom = atom(false);
